<template>
  <div id="information" v-if="currentEvent">
    <div class="title">
      <h2>{{ $t('volunteers.necessary_information') }}</h2>
    </div>
    <div class="form-information">
      <div class="estimate">
        <div class="info">
          <p class="text" v-html="$t(description)"></p>
        </div>
      </div>

      <!-- Day of Arrival -->
      <div class="item" v-if="config.ui.showArrival">
        <div class="label">{{ $t('params.day_of_arrival') }} <span class="obligatory">*</span></div>
        <select
          v-if="config.ui.showDayOfArrival"
          v-model="input.day_of_arrival"
          :class="{ error: error.day_of_arrival }"
        >
          <option v-for="item in config.day_of_arrival" :key="item.key" :value="item.key">
            {{ $t(item.value) + ' ' + formatDate(item.key) }}
          </option>
        </select>
        <v-menu
          v-else
          v-model="dialog_day_arrival"
          autocomplete="new-text"
          :close-on-content-click="false"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
          :class="error.day_of_arrival ? 'error' : ''"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              prepend-icon="event"
              v-model="input.day_of_arrival"
              @click:prepend="dialog_day_arrival = true"
              :placeholder="'DD/MM/YYYY'"
              :class="error.day_of_arrival ? 'error' : ''"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="day_arrival"
            @input="(dialog_day_arrival = false), formatDateArrival()"
          ></v-date-picker>
        </v-menu>
      </div>

      <!-- Arrival Site -->
      <SelectField
        v-if="config.ui.showSite"
        v-model="input.arrival_site"
        :items="config.arrival_site"
        :label="$t('params.arrival_site')"
        :error="error.arrival_site"
        :required="true"
      />

      <!-- Estimated Arrival -->
      <SelectField
        v-if="config.ui.showEstimateArrival"
        v-model="input.estimate_arrival"
        :items="config.estimate_arrival"
        :label="$t('params.estimate_arrival')"
        :error="error.estimate_arrival"
        :required="true"
      />

      <!-- Day of Exit -->
      <div class="item" v-if="config.ui.showExit">
        <div class="label">{{ $t('params.day_of_exit') }} <span class="obligatory">*</span></div>
        <select
          v-if="config.ui.showDayOfExit"
          v-model="input.day_of_exit"
          :class="{ error: error.day_of_exit }"
        >
          <option v-for="item in config.day_of_exit" :key="item.key" :value="item.key">
            {{ $t(item.value) + ' ' + formatDate(item.key) }}
          </option>
        </select>
        <v-menu
          v-else
          v-model="dialog_day_exit"
          autocomplete="new-text"
          :close-on-content-click="false"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
          :class="error.day_exit ? 'error' : ''"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              prepend-icon="event"
              v-model="input.day_of_exit"
              @click:prepend="dialog_day_exit = true"
              :placeholder="'DD/MM/YYYY'"
              :class="error.day_exit ? 'error' : ''"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="day_exit"
            @input="(dialog_day_exit = false), formatDateExit()"
          ></v-date-picker>
        </v-menu>
      </div>

      <!-- Car Section -->
      <div
        class="item" v-if="config.ui.showArrival"
      >
        <div class="label">
          {{ $t("params.take_car") }} <span class="obligatory">*</span>
        </div>
        <v-radio-group v-model="input.have_car" row @change="clearCar">
          <v-radio value="1">
            <template v-slot:label>
              <span :class="{ obligatory: error.have_car }">{{
                $t("si")
              }}</span>
            </template>
          </v-radio>
          <v-radio value="0">
            <template v-slot:label>
              <span :class="{ obligatory: error.have_car }">{{
                $t("no")
              }}</span>
            </template>
          </v-radio>
        </v-radio-group>

        <template v-if="input.have_car == 1">
          <div class="label">
            {{ $t("params.leave_from") }} <span class="obligatory">*</span>
          </div>
          <input
            type="text"
            v-model="input.leave_from"
            :class="{ error: error.leave_from }"
          />

          <div class="label">
            {{ $t("params.car_seats") }} <span class="obligatory">*</span>
          </div>
          <input
            type="number"
            v-model="input.car_seats"
            :class="{ error: error.car_seats }"
          />
        </template>

        <template
          v-if="config.ui.showHelpLeaveFrom && input.have_car == '0'"
        >
          <div class="label">
            {{ $t("params.help_leave_from") }} <span class="obligatory">*</span>
          </div>
          <v-radio-group v-model="input.help_leave_from" row>
            <v-radio value="1">
              <template v-slot:label>
                <span :class="{ obligatory: error.help_leave_from }">{{
                  $t("si")
                }}</span>
              </template>
            </v-radio>
            <v-radio value="0">
              <template v-slot:label>
                <span :class="{ obligatory: error.help_leave_from }">{{
                  $t("no")
                }}</span>
              </template>
            </v-radio>
          </v-radio-group>

          <template v-if="input.help_leave_from == 1">
            <div class="label">
              {{ $t("params.leave_from") }} <span class="obligatory">*</span>
            </div>
            <input
              type="text"
              v-model="input.leave_from"
              :class="{ error: error.leave_from }"
            />
          </template>
        </template>
      </div>

      <!-- CC and Contract -->
      <div class="item" v-if="config.ui.showNumCC">
        <label>{{ $t('params.num_cc') }} <span class="obligatory">*</span></label>
        <input
          class="upper shadow"
          type="text"
          v-model="input.num_cc"
          :class="{ error: error.num_cc }"
        />
      </div>

      <SelectField
        v-if="config.ui.showNumCC"
        v-model="input.contract"
        :items="config.contract"
        :label="$t('params.contract')"
        :error="error.contract"
        :required="true"
      />

      <!-- Allergies -->
      <div class="item" id="alergy">
        <div class="label">{{ $t("params.alergy") }}</div>
        <v-select
          :class="error.alergy ? 'error' : ''"
          v-model="input.alergy"
          :items="Object.values(alergies)"
          item-text="name"
          item-value="id"
          multiple
          :browser-autocomplete="'asd2saS<'"
          ref="alergy"
          id="alergies"
          style="padding: 0"
        >
          <template v-slot:selection="data">
            <v-chip
              :selected="data.selected"
              close
              @input="removeAlergy(data.item)"
            >
              {{ data.item.name }}
            </v-chip>
          </template>
        </v-select>
      </div>

      <!-- Other Allergies -->
      <div class="item">
        <div class="label">{{ $t('params.other_allergies') }}</div>
        <input type="text" v-model="input.other_allergies" />
      </div>

      <!-- Diet -->
      <SelectField
        v-model="input.diet"
        :items="Object.entries(diets).map(([key, val]) => ({ key, value: val.name }))"
        :label="$t('params.diet')"
      />

      <!-- Clothing -->
      <SelectField
        v-model="input.shirt_size"
        :items="shirt_sizes"
        :label="$t('params.clothing_size')"
        :description="$t('info_volunteer_arrival.clothing')"
        :error="error.shirt_size"
        :required="true"
      />

      <!-- Shoes size -->
      <div class="item" v-if="config.ui.showShoesSize">
        <div class="label">
          {{ $t('params.shoes_size') }} <span class="obligatory">*</span>
          <span class="guide" @click="guide"><b>{{ $t('guide') }}</b></span>
        </div>
        <span>{{ $t('info_volunteer_arrival.shoes') }}</span>
        <select v-model="input.shoes_size" :class="{ error: error.shoes_size }">
          <option v-for="item in shoes_sizes" :key="item.id" :value="item.id">
            {{ item.ue }} {{ item.sex == 1 ? $t('man') : $t('woman') }}
          </option>
        </select>
      </div>

      <!-- Years on MIC -->
      <div class="item" v-if="config.ui.showYearsOnMic">
        <div class="label">
          {{ $t('params.years_on_mic') }} <span class="obligatory">*</span>
          <span class="guide"><b>{{ $t('params.years_on_mic_description') }}</b></span>
        </div>
        <input
          type="number"
          v-model="input.years_mic"
          :class="{ error: error.years_mic }"
        />
      </div>

      <!-- Observations -->
      <div class="item">
        <div class="label">{{ $t('params.observations') }}</div>
        <input type="text" v-model="input.observations" />
      </div>

      <div class="actions">
        <div class="btn" id="addedit" @click="update()">
          {{ $t('update') }}
        </div>
      </div>
    </div>

    <SuccessPopup :dialog="success" @close="closePopup" @input="closePopup" />
    <ErrorPopup :dialog="dialog_error" @close="closePopup" @input="closePopup" />
  </div>
</template>

<script>
/* global moment */
/* global isEmpty */
import i18n from '@/i18n'
import eventConfig from '@/data/events_data.js'
import SuccessPopup from '@/components/ui/SuccessPopup.vue'
import ErrorPopup from '@/components/ui/ErrorPopup.vue'
import SelectField from '@/components/ui/SelectField.vue'
import vuetifySelectOutsideClick from '@/mixins/vuetifySelectOutsideClick'

export default {
  name: 'Information',
  mixins: [vuetifySelectOutsideClick],
  components: {
    SelectField,
    SuccessPopup,
    ErrorPopup
  },
  data () {
    return {
      day_arrival: '',
      day_of_arrival: [],
      dialog_day_arrival: false,
      day_exit: '',
      day_of_exit: [],
      dialog_day_exit: false,
      arrival_site: [],
      estimate_arrival: [],
      contract: [],
      input: {
        day_of_arrival: '',
        arrival_site: '',
        contract: '',
        num_cc: '',
        estimate_arrival: '',
        day_of_exit: '',
        shirt_size: '',
        shoes_size: '',
        have_car: '',
        help_leave_from: '',
        leave_from: '',
        car_seats: '',
        b_alergy: '',
        alergy: [],
        other_allergies: '',
        diet: 'Z1NjenJhTWxQSmdjVkYwVG1JMVMrZz09',
        observations: '',
        years_mic: ''
      },
      error: {
        day_of_arrival: false,
        day_of_exit: false,
        arrival_site: false,
        estimate_arrival: false,
        shirt_size: false,
        shoes_size: false,
        have_car: false,
        leave_from: false,
        car_seats: false,
        help_leave_from: false,
        years_mic: false,
        num_cc: false,
        contract: false,
        alergy: false
      },
      success: false,
      dialog_error: false,
      loadDescription: ''
    }
  },
  computed: {
    user () {
      return this.$store.getters['getUser']
    },
    currentEvent () {
    // if nothing is found, return null (or an empty object)
      return this.$store.getters['events/getList'](this.$route.params.event_id) || null
    },
    eventTypeId () {
      // only access .type_id if currentEvent is truthy
      return this.currentEvent
        ? Number(this.currentEvent.type_id)
        : null
    },
    config () {
    // if you don’t have a valid eventTypeId yet, fall back to {}
      return this.eventTypeId != null
        ? eventConfig[this.eventTypeId] || {}
        : {}
    },
    description () {
      return this.loadDescription
    },
    shoes_sizes () {
      return this.$store.getters['getVolunteerShoesSize']
    },
    alergies () {
      return this.$store.getters['getAlergy']
    },
    diets () {
      return this.$store.getters['getDiets']
    },
    shirt_sizes () {
      const all = this.$store.getters['getVolunteerShirtSize']
      if (!this.config.ui.shirtSizeMenOnly) {
        return Object.values(all).map(item => ({
          key: item.id,
          value: item.name
        }))
      }
      // Filter out the shirts that end with '-men'
      return Object.entries(all)
        .filter(([_, shirt]) => shirt.name.endsWith('-men'))
        .map(([key, shirt]) => ({
          key,
          value: shirt.name.replace(/-men$/, '') // removes the "-men"
        }))
    }
  },
  created () {
    if (this.$store.state.tablesLoaded) {
      this.loadEventInformation()
      this.loadVolunteerInformation()
    } else if (!this.$store.state.tablesLoading) {
      this.$store.dispatch('loadTablesRelationship')
    }
  },
  watch: {
    eventTypeId (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadEventInformation()
      }
    },
    config: {
      handler () {
        this.loadEventInformation()
      },
      deep: true
    }
    // input: {
    //   handler () {
    //     console.log('input', this.input)
    //   },
    //   deep: true
    // }
  },
  methods: {
    getVuetifyOutsideClickSelectRefs () {
      return ['alergy']
    },

    loadEventInformation () {
      const cfg = this.config
      this.loadDescription = cfg.descriptionKey || ''
      this.day_of_arrival = cfg.day_of_arrival || []
      this.arrival_site = cfg.arrival_site || []
      this.day_of_exit = cfg.day_of_exit || []
      this.estimate_arrival = cfg.estimate_arrival || []
      this.contract = cfg.contract || []
    },
    loadVolunteerInformation () {
        let self = this;
      this.$store.dispatch('getRelVolunteerEvent', {
        person_id: this.user.person_id,
        event_id: this.$route.params.event_id
      }).then(function () {
          let result = self.$store.getters["getEventInformation"];

          self.error.day_of_arrival = false;
          self.error.arrival_site = false;
          // self.error.check_in = false;
          self.error.estimate_arrival = false;
          self.error.day_of_exit = false;
          self.error.shirt_size = false;
          self.error.shoes_sizes = false;
          self.error.have_car = false;

          if (result.day_of_arrival != null) {
            if (parseInt(self.currentEvent.type_id) != 4) {
              let day_of_arrival = result.day_of_arrival.split(" ");
              let day_of_exit = result.day_of_exit.split(" ");
              self.input.day_of_arrival = day_of_arrival[0];
              self.input.day_of_exit = day_of_exit[0];
              self.parsedExitDate = moment(
                self.input.day_of_exit,
                "YYYY-MM-DD"
              ).format("DD/MM/YYYY");
            } else {
              self.input.day_of_arrival = result.day_of_arrival;
              self.input.day_of_exit = result.day_of_exit;
            }
            self.input.arrival_site = result.arrival_site;
            self.input.estimate_arrival = result.estimate_arrival;
            //self.input.check_in = day_of_arrival[1];
            self.input.shirt_size = result.shirt_size;
            self.input.shoes_size = result.shoes_size;
            self.input.have_car =
              result.have_car != null
                ? (self.input.have_car = "1")
                : (self.input.have_car = "0");
            self.input.leave_from =
              result.leave_from != null ? result.leave_from : "";
            self.input.car_seats =
              result.car_seats != null ? result.car_seats : "";
            self.input.observations =
              result.observations != null ? result.observations : "";

            if (self.input.have_car == 0 && self.input.leave_from != "") {
              console.log(1234);
              self.input.help_leave_from = "1";
            } else {
              self.input.help_leave_from = "0";
            }

            self.input.years_mic =
              result.years_mic != null ? result.years_mic : "";

            self.input.num_cc = result.num_cc != null ? result.num_cc : "";
            self.input.contract =
              result.contract != null ? result.contract : "";

            // MICFootball
            if (
              self.event == "MICFootball" &&
              self.input.day_of_arrival == "0000-00-00"
            ) {
              self.input.day_of_arrival = "others";
            }
            if (
              self.event == "MICFootball" &&
              self.input.day_of_exit == "0000-00-00"
            ) {
              self.input.day_of_exit = "others";
            }
          } else {
            self.input.day_of_arrival = "";
            self.input.arrival_site = "";
            self.input.estimate_arrival = "";
            // self.input.check_in = '';
            self.input.day_of_exit = "";
            self.parsedExitDate = "";
            self.input.shirt_size = "";
            self.input.shoes_size = "";
            self.input.have_car = "";
            self.input.leave_from = "";
            self.input.car_seats = "";
            self.input.observations = "";
          }

          self.input.alergy = result.alergy;
          self.input.other_allergies =
            result.other_allergies != null ? result.other_allergies : "";

          result.diet != null
            ? (self.input.diet = result.diet)
            : (self.input.diet = "Z1NjenJhTWxQSmdjVkYwVG1JMVMrZz09");
        });
    },
    validate () {
      let valid = true
      const rules = [
        { field: 'day_of_arrival', show: 'showArrival' },
        { field: 'arrival_site', show: 'showSite' },
        { field: 'estimate_arrival', show: 'showEstimateArrival' },
        { field: 'day_of_exit', show: 'showExit' },
        { field: 'shirt_size', show: null },
        { field: 'shoes_size', show: 'showShoesSize' },
        { field: 'have_car', show: 'showArrival' }
      ]

      // Validate basic fields
      rules.forEach(({ field, show }) => {
        const shouldCheck = show ? (this.config.ui[show] ? this.config.ui[show] : false) : true
        this.error[field] = !this.input[field] && shouldCheck
        console.log('error', this.error[field])
        console.log('input', this.input[field])
        console.log('shouldCheck', shouldCheck)
        console.log('config.ui[show]', this.config.ui[show])
        console.log('field', field)
        if (this.error[field]) valid = false
      })

      // Validate car-related fields
      if (this.config.ui.showArrival) {
        if (isEmpty(this.input.have_car)) {
          this.error.have_car = true
          valid = false
        } else {
          this.error.have_car = false
          if (this.input.have_car === '1') {
            // User has a car
            this.error.leave_from = isEmpty(this.input.leave_from)
            this.error.car_seats = isEmpty(this.input.car_seats)
            if (this.error.leave_from || this.error.car_seats) valid = false
          } else {
            // User does not have a car
            // If help_leave_from is shown
            if (this.config.ui.showHelpLeaveFrom) {
              this.error.help_leave_from = isEmpty(this.input.help_leave_from)
              if (this.error.help_leave_from) valid = false

              if (this.input.help_leave_from === '1') {
                // User needs help
                this.error.leave_from = isEmpty(this.input.leave_from)
                if (this.error.leave_from) valid = false
              } else if (this.input.help_leave_from === '0') {
                // User does not need help
                this.input.leave_from = ''
                this.error.leave_from = false
              }
            } else {
              // If help_leave_from is not shown, clear the field
              this.error.help_leave_from = false
              this.error.leave_from = false
            }
            this.error.car_seats = false
          }
        }
      }
      console.log('valid', this.input.shirt_size)
      console.log('valid', this.errors)

      return valid
    },
    update () {
      if (!this.validate()) {
        this.dialog_error = true
        return
      }
      if (moment(this.input.day_of_arrival, 'DD/MM/YYYY', true).isValid()) {
        this.input.day_of_arrival = moment(this.input.day_of_arrival, 'DD/MM/YYYY').format('YYYY-MM-DD')
      }
      if (moment(this.input.day_of_exit, 'DD/MM/YYYY', true).isValid()) {
        this.input.day_of_exit = moment(this.input.day_of_exit, 'DD/MM/YYYY').format('YYYY-MM-DD')
      }
      const files = Object.keys(this.input.files || {}).reduce((acc, key) => {
        const fileData = this.input.files[key]
        if (fileData && !fileData.includes('http')) {
          acc[key] = this.$refs[key].files[0]
        }
        return acc
      }, {})
      this.$store.dispatch('updateEventInformation', {
        person_id: this.user.person_id,
        event_id: this.$route.params.event_id,
        input: this.input,
        files
      }).then(() => {
        this.success = true
        this.loadEventInformation()
      })
    },
    formatDate (date) {
      return date && date !== 'others' ? moment(date).format('DD/MM/YYYY') : ''
    },
    formatDateArrival () {
      this.input.day_of_arrival = moment(this.day_arrival).format('DD/MM/YYYY')
    },
    formatDateExit () {
      this.input.day_of_exit = moment(this.day_exit).format('DD/MM/YYYY')
    },
    guide () {
      window.open(
        process.env.VUE_APP_APP_URL + 'talles/shoes_' + i18n.locale + '.pdf'
      )
    },
    closePopup () {
      this.success = false
      this.dialog_error = false
    },
    removeAlergy (item) {
      let index = this.input.alergy.indexOf(item.id)
      if (index >= 0) {
        let aux = JSON.parse(JSON.stringify(this.input.alergy))
        aux.splice(index, 1)
        this.input.alergy = aux
      }
    },
    clearCar () {
      this.input.leave_from = ''
      this.input.car_seats = ''
      this.input.help_leave_from = ''
      this.error.leave_from = false
      this.error.car_seats = false
      this.error.help_leave_from = false
    }
  }
}
</script>

<style lang="scss" src="@/styles/_information.scss">
/* you can leave this empty or add component-specific overrides */
</style>

<style lang="scss" scoped>
.actions {
  padding: 25px;
  margin: 0 auto;
  margin-bottom: 25px;
  text-align: center;

  .btn {
    width: calc(50% - 20px);
    margin: 0 10px;
    display: inline-block;
    min-width: 40px;
    padding: 0 35px;
    outline: none;
    border: none;
    cursor: pointer;
    text-align: center;
    font-size: 16px;
    border-radius: 3px;
    -webkit-transition: all 0.5s;
    transition: all 0.5s;
    background-color: #172654;
    color: #fff;
    height: 40px;
    line-height: 40px;
    text-transform: uppercase;
    background-color: #fc8926;
    font-family: AvantGarde-Bold;
    max-width: 300px;
    transition: background-color 0.5s ease;

    &#addedit {
      border: 1px solid #fc8926;

      &:hover {
        background-color: #e78026;
        border-color: #e78026;
      }
    }

    &#cancel {
      background-color: transparent;
      color: #c55c00;
      border: 1px solid #fc8926;

      &:hover {
        background-color: #fc8a2623 !important;
      }
    }
  }
}

@include media("<=desktop") {
  .actions {
    padding: 25px 0px;

    .btn {
      padding: 0 15px;
    }
  }
}
</style>
